import { useContext } from 'react'

import classNames from 'classnames'

import { library } from '@fortawesome/fontawesome-svg-core'
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Trans } from '@lingui/macro'
import AppBody from 'Root/App/Body'
import Button from 'Root/App/Button'
import { Context as CollectionContext } from 'Root/Pages/Collection/Component/Context'
import canAccessContent from 'lib/can-access-content'

import styles from './index.module.css'

library.add(faArrowLeft, faArrowRight)

const getAdjacentElementStatus = (element) => {
  if (element === undefined) {
    return 'needQuery'
  }

  if (element === null) {
    return 'notPresent'
  }

  return 'elementReady'
}

const Body = ({
  content,
  isCompleted,
  collectionPreparatory,
  collectionVisibility,
  children,
}) => {
  const { collectionSlug, nextElement, previousElement } =
    useContext(CollectionContext)

  const showNext =
    canAccessContent(
      nextElement?.visibilityForCollection,
      content.canManage,
      collectionVisibility,
      content.collectionCanRead
    ) &&
    (!collectionPreparatory ||
      (!!collectionPreparatory &&
        (!!content?.hasUserVisited ||
          !!content?.hasUserCompleted ||
          !!isCompleted ||
          ['TextContent', 'PdfContent', 'ImageContent', 'LinkContent'].includes(
            content.__typename
          ))))

  const showPrev = canAccessContent(
    previousElement?.visibilityForCollection,
    content.canManage,
    collectionVisibility,
    content.collectionCanRead
  )

  const previousElementStatus = getAdjacentElementStatus(previousElement)
  const nextElementStatus = getAdjacentElementStatus(nextElement)

  const previousElementButtonStyles = [
    styles.button,
    {
      'pointer-events-none animate-pulse':
        previousElementStatus === 'needQuery',
    },
    {
      'pointer-events-none opacity-40':
        previousElementStatus === 'notPresent' || !showPrev,
    },
  ]

  const nextElementButtonStyles = [
    styles.button,
    {
      'pointer-events-none animate-pulse': nextElementStatus === 'needQuery',
    },
    {
      'pointer-events-none opacity-40':
        nextElementStatus === 'notPresent' || !showNext,
    },
  ]

  const previousContentURL =
    Boolean(previousElement) && showPrev
      ? `/cc/${collectionSlug}/${previousElement.slug}`
      : ''

  const nextContentURL =
    Boolean(nextElement) && showNext
      ? `/cc/${collectionSlug}/${nextElement.slug}`
      : ''

  return (
    <AppBody>
      <div className={styles.container}>
        <div className={styles.columnMobile}>
          <Button
            anchor
            className={classNames(previousElementButtonStyles)}
            tabIndex={
              previousElementStatus === 'notPresent' || !showPrev ? -1 : 0
            }
            size="M"
            to={previousContentURL}
          >
            <div className="flex flex-row gap-2">
              <div>
                <FontAwesomeIcon icon={['fas', 'arrow-left']} />
              </div>
              <div className="leading-6">
                <Trans>Precedente</Trans>
              </div>
            </div>
          </Button>
        </div>
        <div className={styles.columnMobile}>
          <Button
            anchor
            className={classNames(nextElementButtonStyles)}
            tabIndex={nextElementStatus === 'notPresent' || !showNext ? -1 : 0}
            size="M"
            to={nextContentURL}
          >
            <div className="flex flex-row gap-2">
              <div className="leading-6">
                <Trans>Successivo</Trans>
              </div>
              <div>
                <FontAwesomeIcon icon={['fas', 'arrow-right']} />
              </div>
            </div>
          </Button>
        </div>
        <div className={styles.columnDesktop}>
          <Button
            anchor
            icon={<FontAwesomeIcon icon={['fas', 'arrow-left']} />}
            className={classNames(previousElementButtonStyles)}
            tabIndex={
              previousElementStatus === 'notPresent' || !showPrev ? -1 : 0
            }
            size="L"
            subtype="circle"
            to={previousContentURL}
          />
        </div>
        {children}
        <div className={styles.columnDesktop}>
          <Button
            anchor
            icon={<FontAwesomeIcon icon={['fas', 'arrow-right']} />}
            className={classNames(nextElementButtonStyles)}
            tabIndex={nextElementStatus === 'notPresent' || !showNext ? -1 : 0}
            size="L"
            subtype="circle"
            to={nextContentURL}
          />
        </div>
      </div>
    </AppBody>
  )
}

export default Body
